// TOVIS · Módulo TUBITOS · chamadas ao motor (/api/tubitos) e apoio visual.
// As REGRAS ficam no motor (app/utils/tubitos_calculo.py). Aqui só chamada e formatação de tela.
import { API_URL } from './config'; // [AJUSTAR] necessário para a BASE abaixo (mesmo import do liraaApi.ts)

export type AvisoTubito = {
  tipo: 'pulo' | 'repetido' | 'fora_de_ordem' | 'sem_tubito';
  texto: string;
  numeros: number[];
  fad01_id?: number;
  fad01_id_outro?: number;
  data_texto?: string;
  agente_id?: number;
  nome?: string;
};
export type ResumoAgente = {
  agente_id: number; nome: string; situacao: 'ok' | 'conferir' | 'sem_registro';
  ultimo: number | null; proximo: number | null; data_ultimo: string | null; data_ultimo_texto: string;
  fad01_id_ultimo: number | null; tubitos_ultimo_texto: string; perto_999: boolean; avisos: AvisoTubito[];
};
export type Painel = { agentes: ResumoAgente[]; avisos: AvisoTubito[]; atualizado_ate: string | null; atualizado_ate_texto: string; gerado_em: string };
export type ItemHistorico = {
  fad01_id: number; data: string; data_texto: string; inicial: number; qtd: number; eh_pe: boolean;
  numeros: number[]; faixa_texto: string; tubitos_texto: string; situacao: 'primeiro' | 'ok' | 'conferir';
  avisos: AvisoTubito[]; pulados: number[];
};
export type Historico = { agente: ResumoAgente; itens: ItemHistorico[]; total: number };
export type InfoBoletim = {
  fad01_id: number; agente_id: number | null; agente_nome: string | null; data: string; data_texto: string;
  qtd: number; eh_pe: boolean; numero_inicial: number | null; numeros: number[]; faixa_texto: string;
  tubitos_texto: string; situacao: string | null; avisos: AvisoTubito[]; sugestao: number | null;
  mostrar_campo: boolean; salvo?: boolean;
};

const BASE = API_URL; // [AJUSTAR] mesma base de URL que o liraaApi.ts usa
function lerToken(): string | null {
  try { return localStorage.getItem('tovis_token'); } catch { return null; } // [AJUSTAR] mesmo lugar do token que o liraaApi.ts usa
}

async function chamar<T>(metodo: string, caminho: string, corpo?: unknown): Promise<T> {
  const tk = lerToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (tk) headers.Authorization = `Bearer ${tk}`;
  const r = await fetch(BASE + caminho, { method: metodo, headers, body: corpo === undefined ? undefined : JSON.stringify(corpo) });
  if (!r.ok) {
    let msg = `Erro ${r.status}`;
    try {
      const j = await r.json();
      if (j && j.detail) msg = typeof j.detail === 'string' ? j.detail : JSON.stringify(j.detail);
    } catch { /* sem corpo */ }
    throw new Error(msg);
  }
  return (await r.json()) as T;
}

export const tubitosApi = {
  painel: () => chamar<Painel>('GET', '/api/tubitos/painel'),
  mensagem: () => chamar<{ texto: string }>('GET', '/api/tubitos/mensagem'),
  historico: (agenteId: number, limite = 20) => chamar<Historico>('GET', `/api/tubitos/agente/${agenteId}?limite=${limite}`),
  boletim: (fad01Id: number) => chamar<InfoBoletim>('GET', `/api/tubitos/boletim/${fad01Id}`),
  conferir: (fad01Id: number, n: number) => chamar<InfoBoletim>('POST', `/api/tubitos/boletim/${fad01Id}/conferir`, { numero_inicial: n }),
  salvar: (fad01Id: number, n: number) => chamar<InfoBoletim>('PUT', `/api/tubitos/boletim/${fad01Id}`, { numero_inicial: n }),
  remover: (fad01Id: number) => chamar<{ removido: boolean }>('DELETE', `/api/tubitos/boletim/${fad01Id}`),
};

export const TUBITOS_COR = '#0F766E';
export const C = {
  texto: '#17202A', suave: '#33404D', fraco: '#4A5562', borda: '#DDE2E8', fundo: '#F4F6F8',
  ambar: '#E8B85A', ambarFundo: '#FFF4DC', ambarTexto: '#5C3A00', seloAmbar: '#FDE7B8', seloAmbarTexto: '#6B4200',
  azulBorda: '#A9C6F0', azulFundo: '#EEF5FF', azulTexto: '#123C7A', seloAzul: '#DCEBFF',
  verdeFundo: '#DFF3EF', verdeChip: '#EEF6F4', verdeTexto: '#0B5A54', cinzaSelo: '#E9ECF0', cinzaTexto: '#3A4552',
  cinzaBorda: '#B8C0CA', vermelho: '#B42318',
};

export function textoNumeros(n: number[]): string {
  if (!n || n.length === 0) return '';
  if (n.length === 1) return String(n[0]);
  if (n.length === 2) return `${n[0]} e ${n[1]}`;
  const seguidos = n.every((v, k) => k === 0 || v === (n[k - 1] >= 999 ? 1 : n[k - 1] + 1));
  if (seguidos) return `${n[0]} a ${n[n.length - 1]}`;
  return `${n.slice(0, -1).join(', ')} e ${n[n.length - 1]}`;
}

export function rotuloCurto(a: AvisoTubito): string {
  if (a.tipo === 'pulo') return `pulou ${textoNumeros(a.numeros)}`;
  if (a.tipo === 'repetido') return a.numeros.length > 1 ? `números ${textoNumeros(a.numeros)} repetidos` : `número ${a.numeros[0]} repetido`;
  if (a.tipo === 'fora_de_ordem') return `fora da sequência (${a.numeros[0]})`;
  return 'nº sem tubito no boletim';
}

export function rotuloSituacao(situacao: string, avisos: AvisoTubito[]): { texto: string; fundo: string; cor: string } {
  if (situacao === 'ok') return { texto: 'Em sequência', fundo: C.verdeFundo, cor: C.verdeTexto };
  if (situacao === 'primeiro') return { texto: '1º registro', fundo: C.cinzaSelo, cor: C.cinzaTexto };
  const t = avisos[0]?.tipo;
  const texto = t === 'pulo' ? 'Pulou números' : t === 'repetido' ? 'Repetido' : t === 'fora_de_ordem' ? 'Fora de ordem' : 'Conferir';
  return { texto, fundo: C.seloAmbar, cor: C.seloAmbarTexto };
}

export async function copiarTexto(texto: string): Promise<boolean> {
  try { await navigator.clipboard.writeText(texto); return true; } catch { /* tenta o jeito antigo */ }
  try {
    const t = document.createElement('textarea');
    t.value = texto; t.style.position = 'fixed'; t.style.opacity = '0';
    document.body.appendChild(t); t.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(t);
    return ok;
  } catch { return false; }
}
